import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useCompany } from "../../hooks/useCompany";
import { supabase } from "../../lib/supabase";
import { AccountApiDocsReference } from "./AccountApiDocsReference";

type CompanyApiKeyRow = {
  id: string;
  label: string | null;
  secret_hint: string;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
};

const PUBLIC_BOOKINGS_PATH = "/functions/v1/api-public-bookings";
const PUBLIC_PRODUCTS_PATH = "/functions/v1/api-public-products";

export function AccountApiAccessTab() {
  const { t } = useTranslation();
  const { selectedCompany } = useCompany();
  const [keys, setKeys] = useState<CompanyApiKeyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");

  const loadKeys = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "company-api-keys",
        {
          body: { action: "list", company_id: selectedCompany },
        }
      );
      if (error) throw error;
      if (data && typeof data === "object" && "error" in data) {
        throw new Error(String((data as { error: string }).error));
      }
      setKeys((data as { keys: CompanyApiKeyRow[] })?.keys ?? []);
    } catch (e) {
      console.error(e);
      message.error(t("account.apiKeysLoadError"));
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, t]);

  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  const showCreatedModal = (token: string) => {
    Modal.success({
      title: t("account.apiKeysCreatedTitle"),
      width: 560,
      content: (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Typography.Text type="warning">
            {t("account.apiKeysCreatedBody")}
          </Typography.Text>
          <Input.TextArea
            readOnly
            autoSize={{ minRows: 2, maxRows: 4 }}
            value={token}
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
          <Button
            type="primary"
            onClick={() => {
              void navigator.clipboard.writeText(token);
              message.success(t("common.copied"));
            }}
          >
            {t("account.apiKeysCopy")}
          </Button>
        </Space>
      ),
    });
  };

  const handleCreate = async () => {
    if (!selectedCompany) return;
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "company-api-keys",
        {
          body: {
            action: "create",
            company_id: selectedCompany,
            label: label.trim() || undefined,
          },
        }
      );
      if (error) throw error;
      if (data && typeof data === "object" && "error" in data) {
        throw new Error(String((data as { error: string }).error));
      }
      const token = (data as { token?: string })?.token;
      if (!token) throw new Error("No token");
      setLabel("");
      showCreatedModal(token);
      await loadKeys();
    } catch (e) {
      console.error(e);
      message.error(t("account.apiKeysCreateError"));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    if (!selectedCompany) return;
    try {
      const { data, error } = await supabase.functions.invoke(
        "company-api-keys",
        {
          body: {
            action: "revoke",
            company_id: selectedCompany,
            key_id: keyId,
          },
        }
      );
      if (error) throw error;
      if (data && typeof data === "object" && "error" in data) {
        throw new Error(String((data as { error: string }).error));
      }
      await loadKeys();
    } catch (e) {
      console.error(e);
      message.error(t("account.apiKeysRevokeError"));
    }
  };

  const columns: ColumnsType<CompanyApiKeyRow> = [
    {
      title: t("account.apiKeysColumnLabel"),
      dataIndex: "label",
      key: "label",
      render: (v: string | null) => v || "—",
    },
    {
      title: t("account.apiKeysColumnHint"),
      dataIndex: "secret_hint",
      key: "secret_hint",
      render: (h: string) => `****${h}`,
    },
    {
      title: t("account.apiKeysColumnCreated"),
      dataIndex: "created_at",
      key: "created_at",
      render: (d: string) => dayjs(d).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: t("account.apiKeysColumnStatus"),
      key: "status",
      render: (_, row) =>
        row.revoked_at ? (
          <Tag>{t("account.apiKeysRevoked")}</Tag>
        ) : (
          <Tag color="green">{t("account.apiKeysActive")}</Tag>
        ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 120,
      render: (_, row) =>
        row.revoked_at ? null : (
          <Popconfirm
            title={t("account.apiKeysRevokeConfirm")}
            onConfirm={() => void handleRevoke(row.id)}
            okText={t("common.yes")}
            cancelText={t("common.no")}
          >
            <Button type="link" danger size="small">
              {t("account.apiKeysRevoke")}
            </Button>
          </Popconfirm>
        ),
    },
  ];

  const baseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const origin = baseUrl.replace(/\/$/, "");
  const bookingsUrl = `${origin}${PUBLIC_BOOKINGS_PATH}`;
  const productsUrl = `${origin}${PUBLIC_PRODUCTS_PATH}`;

  const bookingsCurl = `curl -sS "${bookingsUrl}?limit=50&offset=0" \\
  -H "Authorization: Bearer <api_key>"`;

  const productsCurl = `curl -sS "${productsUrl}?limit=100&offset=0" \\
  -H "Authorization: Bearer <api_key>"`;

  if (!selectedCompany) {
    return <Alert type="info" message={t("account.apiSelectCompany")} />;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={t("account.apiKeysTitle")}>
        <Typography.Paragraph>
          {t("account.apiKeysIntro")}
        </Typography.Paragraph>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder={t("account.apiKeysLabelPlaceholder")}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{ width: 280 }}
            aria-label={t("account.apiKeysLabelOptional")}
          />
          <Button
            type="primary"
            onClick={() => void handleCreate()}
            loading={creating}
          >
            {t("account.apiKeysGenerate")}
          </Button>
        </Space>
        <Table<CompanyApiKeyRow>
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={keys}
          pagination={false}
        />
      </Card>

      <Typography.Title level={5} style={{ marginBottom: 12 }}>
        {t("account.apiDocsTitle")}
      </Typography.Title>
      <AccountApiDocsReference
        bookingsUrl={bookingsUrl}
        bookingsCurl={bookingsCurl}
        productsUrl={productsUrl}
        productsCurl={productsCurl}
      />
    </Space>
  );
}
